import { FC } from "react";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CargoColumnType, CargoColumnCreateModel } from "@app/provider-sets/cargoColumnTypes";

interface CargoColumnRowProps {
  index: number;
  column: CargoColumnCreateModel;
  onChange: (updated: CargoColumnCreateModel) => void;
  onRemove: () => void;
  showRemove: boolean;
}

const dataTypeOptions: DropdownItem<CargoColumnType>[] = [
  { key: CargoColumnType.String, name: "String" },
  { key: CargoColumnType.Int, name: "Int" },
  { key: CargoColumnType.Bool, name: "Bool" },
  { key: CargoColumnType.Decimal, name: "Decimal" },
  { key: CargoColumnType.DateTime, name: "DateTime" },
];

const CargoColumnRow: FC<CargoColumnRowProps> = ({ index, column, onChange, onRemove, showRemove }) => {
  const internalName = `CARGO${String(index + 1).padStart(3, "0")}`;

  return (
    <div className="cargo-column-row">
      <div className="row mb-0">
        <div className="col-auto">
          <QESTextInput
            testId={`cargo-column-row__internal-name--${index}`}
            label="Internal Name"
            value={internalName}
            disabled={true}
            size={10}
            onChange={() => {}}
          />
        </div>
        <div className="col">
          <QESTextInput
            testId={`cargo-column-row__external-name--${index}`}
            label="External Name"
            value={column.name}
            maxLength={255}
            required={true}
            isValid={!!column.name}
            onChange={(e) => onChange({ ...column, name: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-2">
        <div className="col">
          <QESSelect
            testId={`cargo-column-row__data-type--${index}`}
            label="Data Type"
            value={column.type}
            required={true}
            isValid={column.type !== ""}
            placeholder="Please Select a Data Type"
            onChange={(e) => onChange({ ...column, length: null, precision: null, scale: null, type: e.target.value as CargoColumnType | "" })}
          >
            {dataTypeOptions.map((item) => (
              <option key={item.key} value={item.key}>{item.name}</option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESTextInput
            testId={`cargo-column-row__length--${index}`}
            label="Length"
            type="number"
            value={column.length ?? ""}
            required={column.type === CargoColumnType.String}
            isValid={column.type !== CargoColumnType.String || column.length !== null}
            disabled={column.type !== CargoColumnType.String}
            onChange={(e) => onChange({ ...column, length: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
        <div className="col">
          <QESTextInput
            testId={`cargo-column-row__scale--${index}`}
            label="Scale"
            type="number"
            value={column.scale ?? ""}
            required={column.type === CargoColumnType.Decimal}
            isValid={column.type !== CargoColumnType.Decimal || column.scale !== null}
            disabled={column.type !== CargoColumnType.Decimal}
            onChange={(e) => onChange({ ...column, scale: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
        <div className="col">
          <QESTextInput
            testId={`cargo-column-row__precision--${index}`}
            label="Precision"
            type="number"
            value={column.precision ?? ""}
            required={column.type === CargoColumnType.Decimal}
            isValid={column.type !== CargoColumnType.Decimal || column.precision !== null}
            disabled={column.type !== CargoColumnType.Decimal}
            onChange={(e) => onChange({ ...column, precision: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
      </div>
      {showRemove && (
        <div className="row mb-2">
          <div className="col">
            <button
              type="button"
              data-testid="cargo-column-row__remove"
              className="qes-button qes-button-secondary danger p-0"
              onClick={onRemove}
            >
              Remove Cargo Column
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CargoColumnRow;
